import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { useI18n } from "../i18n";
import aiBubble from "../assets/4th_block/1.png";
import editorResult from "../assets/4th_block/4.png";
import capsules from "../assets/4th_block/3.png";
import allergies from "../assets/4th_block/img.png";
import gemini2 from "../assets/4th_block/5.png";

type Item = {
  key: string;
  title: string;
  desc: string;
  image: string;
  icon: string;
  // Per-item positioning — tweak each independently.
  // Applied to the <motion.img> rendered as background media.
  imageClass: string;
};

export function Inside() {
  const { t } = useI18n();
  const [active, setActive] = useState(0);

  const items: Item[] = [
    {
      key: "ai",
      title: t("inside.i1.title"),
      desc: t("inside.i1.desc"),
      image: aiBubble,
      icon: aiBubble,
      imageClass:
        "absolute right-[-30%] bottom-[-20%] h-[115%] w-[100%] rotate-[-113deg] scale-x-[-1] object-contain object-bottom-left select-none"
    },
    {
      key: "allergens",
      title: t("inside.i2.title"),
      desc: t("inside.i2.desc"),
      image: allergies,
      icon: allergies,
      imageClass:
        "absolute right-[-30%] bottom-[-5%] h-[115%] w-[100%] rotate-[0deg] object-cover select-none",
    },
    {
      key: "schedule",
      title: t("inside.i3.title"),
      desc: t("inside.i3.desc"),
      image: capsules,
      icon: capsules,
      imageClass:
        "absolute right-[0%] bottom-[-10%] h-[115%] w-[65%] object-contain object-right-bottom select-none",
    },
    {
      key: "packaging",
      title: t("inside.i4.title"),
      desc: t("inside.i4.desc"),
      image: editorResult,
      icon: editorResult,
      imageClass:
        "absolute right-[-10%] bottom-[-15%] h-[120%] w-[70%] object-contain object-bottom-right select-none",
    },
    {
      key: "daynight",
      title: t("inside.i5.title"),
      desc: t("inside.i5.desc"),
      image: gemini2,
      icon: gemini2,
      imageClass:
        "absolute right-[-5%] top-[10%] h-[100%] w-[65%] object-contain object-right select-none",
    },
    {
      key: "doctor",
      title: t("inside.i6.title"),
      desc: t("inside.i6.desc"),
      image: gemini2,
      icon: gemini2,
      imageClass:
        "absolute right-[2%] bottom-[0%] h-[105%] w-[60%] object-contain object-right-bottom select-none",
    },
  ];

  const current = items[active]!;

  return (
    <section className="relative bg-[#FCFFF5] overflow-hidden">
      <div className="relative mx-auto max-w-[1440px] min-h-[560px] sm:min-h-[680px] md:min-h-[820px] lg:min-h-[860px] px-5 sm:px-8 md:px-10 py-14 md:py-20">
        <div className="pointer-events-none absolute inset-0">
          <AnimatePresence mode="sync">
            <motion.img
              key={current.key}
              src={current.image}
              alt=""
              initial={{ opacity: 0, scale: 1.04 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className={current.imageClass}
              draggable={false}
            />
          </AnimatePresence>
        </div>

        {/* Content */}
        <div className="relative z-10 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-6">
            <AnimatePresence mode="wait">
              <motion.h2
                key={current.key}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.5, ease: "easeOut" }}
                className="font-serif text-[26px] sm:text-[32px] md:text-[38px] lg:text-[44px] leading-[1.1] text-[var(--color-ink)] max-w-[520px]"
              >
                {current.title}
              </motion.h2>
            </AnimatePresence>
          </div>

          <div className="hidden md:block md:col-span-3" />

          <div className="col-span-12 md:col-span-3 md:justify-self-end">
            <AnimatePresence mode="wait">
              <motion.p
                key={current.key}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.5, ease: "easeOut", delay: 0.05 }}
                className="text-[13px] leading-[1.5] text-[var(--color-ink)]/60 max-w-[260px]"
              >
                {current.desc}
              </motion.p>
            </AnimatePresence>
          </div>
        </div>

        {/* Selector dots */}
        <div className="absolute left-5 sm:left-8 md:left-10 right-5 sm:right-8 md:right-auto bottom-8 sm:bottom-12 md:bottom-20 z-10 flex flex-wrap gap-2 sm:gap-3 md:gap-4">
          {items.map((item, i) => {
            const isActive = i === active;
            return (
              <button
                key={item.key}
                type="button"
                aria-label={item.title}
                aria-pressed={isActive}
                onClick={() => setActive(i)}
                className={`relative h-10 w-10 sm:h-12 sm:w-12 md:h-16 md:w-16 lg:h-20 lg:w-20 rounded-full overflow-hidden transition-all duration-300 ${
                  isActive
                    ? "ring-2 ring-[var(--color-ink)]/80 scale-105"
                    : "ring-1 ring-[var(--color-ink)]/15 opacity-70 hover:opacity-100"
                }`}
              >
                <img
                  src={item.icon}
                  alt=""
                  className="h-full w-full object-cover"
                  draggable={false}
                />
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
